// src/components/game/KeyboardShortcuts.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useScore } from '../../context/ScoreContext';

const KeyboardShortcuts = ({ isDarkMode }) => {
  const { updateScore, getScore } = useScore();
  const [bestScore, setBestScore] = useState(getScore('keyboard'));

  const shortcuts = [
    { key: 'Ctrl+C', name: 'Copy', icon: '📋', category: 'Basic' },
    { key: 'Ctrl+V', name: 'Paste', icon: '📄', category: 'Basic' },
    { key: 'Ctrl+Z', name: 'Undo', icon: '↩️', category: 'Basic' },
    { key: 'Ctrl+S', name: 'Save', icon: '💾', category: 'Basic' },
    { key: 'Ctrl+F', name: 'Find', icon: '🔍', category: 'Basic' },
    { key: 'Ctrl+A', name: 'Select All', icon: '✅', category: 'Basic' },
    { key: 'Ctrl+X', name: 'Cut', icon: '✂️', category: 'Basic' },
    { key: 'Ctrl+P', name: 'Print', icon: '🖨️', category: 'Basic' },
    { key: 'Ctrl+Shift+Esc', name: 'Task Manager', icon: '⚙️', category: 'Advanced' },
    { key: 'Alt+Tab', name: 'Switch Window', icon: '🔄', category: 'Advanced' },
    { key: 'Win+D', name: 'Show Desktop', icon: '🖥️', category: 'Windows' },
    { key: 'Win+E', name: 'File Explorer', icon: '📁', category: 'Windows' },
  ];

  const [current, setCurrent] = useState(0);
  const [score, setScore] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [shuffledShortcuts, setShuffledShortcuts] = useState([]);
  const [time, setTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(() => {
    const saved = localStorage.getItem('keyboardBestStreak');
    return saved ? parseInt(saved) : 0;
  });
  const [mode, setMode] = useState('key-to-name'); // 'key-to-name' or 'name-to-key'

  useEffect(() => {
    startGame();
  }, []);

  useEffect(() => {
    let timer;
    if (isPlaying && !showResult) {
      timer = setInterval(() => setTime(t => t + 1), 1000);
    }
    return () => clearInterval(timer);
  }, [isPlaying, showResult]);

  const startGame = () => {
    const shuffled = [...shortcuts].sort(() => Math.random() - 0.5);
    setShuffledShortcuts(shuffled);
    setCurrent(0);
    setScore(0);
    setShowResult(false);
    setSelected(null);
    setAnswered(false);
    setTime(0);
    setIsPlaying(true);
    setStreak(0);
    generateOptions(shuffled[0]);
  };

  const generateOptions = (correct) => {
    let opts;
    if (mode === 'key-to-name') {
      opts = [correct.name];
      const otherNames = shortcuts.filter(s => s.name !== correct.name).map(s => s.name);
      while (opts.length < 4) {
        const random = otherNames[Math.floor(Math.random() * otherNames.length)];
        if (!opts.includes(random)) {
          opts.push(random);
        }
      }
    } else {
      opts = [correct.key];
      const otherKeys = shortcuts.filter(s => s.key !== correct.key).map(s => s.key);
      while (opts.length < 4) {
        const random = otherKeys[Math.floor(Math.random() * otherKeys.length)];
        if (!opts.includes(random)) {
          opts.push(random);
        }
      }
    }
    setOptions(opts.sort(() => Math.random() - 0.5));
  };

  const handleAnswer = (selectedValue) => {
    if (answered) return;
    setSelected(selectedValue);
    setAnswered(true);
    setIsPlaying(true);

    const correct = shuffledShortcuts[current];
    const isCorrect = mode === 'key-to-name' 
      ? selectedValue === correct.name 
      : selectedValue === correct.key;

    if (isCorrect) {
      setScore(score + 1);
      setStreak(prev => {
        const newStreak = prev + 1;
        if (newStreak > bestStreak) {
          setBestStreak(newStreak);
          localStorage.setItem('keyboardBestStreak', newStreak.toString());
        }
        return newStreak;
      });
    } else {
      setStreak(0);
    }

    setTimeout(() => {
      if (current < shuffledShortcuts.length - 1) {
        const next = current + 1;
        setCurrent(next);
        setSelected(null);
        setAnswered(false);
        generateOptions(shuffledShortcuts[next]);
      } else {
        setShowResult(true);
        setIsPlaying(false);
        const finalScore = score + (isCorrect ? 1 : 0);
        if (finalScore > bestScore) {
          updateScore('keyboard', finalScore);
          setBestScore(getScore('keyboard'));
        } else {
          updateScore('keyboard', finalScore);
          setBestScore(getScore('keyboard'));
        }
      }
    }, 1500);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStreakEmoji = () => {
    if (streak >= 5) return '🔥';
    if (streak >= 3) return '⚡';
    if (streak >= 2) return '💪';
    return '📈';
  };

  const pageStyle = {
    minHeight: '100vh',
    background: isDarkMode 
      ? 'linear-gradient(135deg, #0a0a1a 0%, #1a1a2e 100%)' 
      : 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
    padding: '20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const containerStyle = {
    maxWidth: '550px',
    width: '100%',
    background: isDarkMode 
      ? 'rgba(26, 26, 46, 0.9)' 
      : 'rgba(255, 255, 255, 0.9)',
    padding: 'clamp(20px, 4vw, 30px)',
    borderRadius: '20px',
    backdropFilter: 'blur(20px)',
    boxShadow: isDarkMode 
      ? '0 20px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)' 
      : '0 20px 60px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.5)',
    border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
    animation: 'slideUp 0.6s ease-out',
    textAlign: 'center'
  };

  return (
    <div style={pageStyle}>
      <style>
        {`
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(30px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
          @keyframes shimmer {
            0% { background-position: -200% center; }
            100% { background-position: 200% center; }
          }
          @keyframes keyPress {
            0% { transform: scale(1); }
            50% { transform: scale(0.95); }
            100% { transform: scale(1); }
          }
          .game-title {
            background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-size: 200% auto;
            animation: shimmer 3s linear infinite;
          }
          .streak-fire {
            animation: pulse 0.5s infinite;
          }
          .key-display {
            animation: keyPress 0.1s ease-out;
          }
        `}
      </style>

      <div style={containerStyle}>
        <Link to="/games" style={{
          display: 'inline-block',
          padding: '8px 20px',
          marginBottom: '20px',
          background: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
          color: isDarkMode ? '#fff' : '#000',
          textDecoration: 'none',
          borderRadius: '12px',
          transition: 'all 0.3s',
          backdropFilter: 'blur(10px)',
          border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)'
        }}
        onMouseEnter={(e) => {
          e.target.style.transform = 'translateX(-5px)';
          e.target.style.background = isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)';
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = 'translateX(0)';
          e.target.style.background = isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)';
        }}>
          ← უკან
        </Link>

        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '15px',
          flexWrap: 'wrap',
          gap: '10px'
        }}>
          <h2 style={{ margin: 0, fontSize: 'clamp(20px, 3vw, 28px)' }}>
            <span className="game-title">⌨️ Shortcuts</span>
          </h2>
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ color: '#4ade80', fontWeight: 'bold' }}>
              🏆 {bestScore}
            </span>
            <span style={{ color: isDarkMode ? '#aaa' : '#666' }}>
              🎯 {score}
            </span>
          </div>
        </div>

        {!showResult ? (
          <>
            {/* Mode Toggle */}
            <div style={{
              display: 'flex',
              gap: '8px',
              marginBottom: '15px',
              justifyContent: 'center'
            }}>
              <button
                onClick={() => {
                  setMode('key-to-name');
                  startGame();
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: '20px',
                  border: '2px solid',
                  borderColor: mode === 'key-to-name' ? '#667eea' : (isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'),
                  background: mode === 'key-to-name' ? '#667eea' : 'transparent',
                  color: mode === 'key-to-name' ? '#fff' : (isDarkMode ? '#aaa' : '#666'),
                  fontSize: '13px',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  fontWeight: mode === 'key-to-name' ? 'bold' : 'normal'
                }}
              >
                ⌨️ → 📝
              </button>
              <button
                onClick={() => {
                  setMode('name-to-key');
                  startGame();
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: '20px',
                  border: '2px solid',
                  borderColor: mode === 'name-to-key' ? '#667eea' : (isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'),
                  background: mode === 'name-to-key' ? '#667eea' : 'transparent',
                  color: mode === 'name-to-key' ? '#fff' : (isDarkMode ? '#aaa' : '#666'),
                  fontSize: '13px',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  fontWeight: mode === 'name-to-key' ? 'bold' : 'normal'
                }}
              >
                📝 → ⌨️
              </button>
            </div>

            {/* Stats */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(70px, 1fr))',
              gap: '8px',
              marginBottom: '20px',
              padding: '15px',
              background: isDarkMode ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.03)',
              borderRadius: '12px',
              textAlign: 'center'
            }}>
              <div>
                <div style={{ fontSize: '18px' }}>📝</div>
                <div style={{ fontSize: '14px', color: isDarkMode ? '#fff' : '#000' }}>
                  {current + 1}/{shuffledShortcuts.length}
                </div>
                <div style={{ fontSize: '9px', color: isDarkMode ? '#666' : '#999' }}>კითხვა</div>
              </div>
              <div>
                <div style={{ fontSize: '18px' }}>⏱️</div>
                <div style={{ fontSize: '14px', color: isDarkMode ? '#fff' : '#000' }}>{formatTime(time)}</div>
                <div style={{ fontSize: '9px', color: isDarkMode ? '#666' : '#999' }}>დრო</div>
              </div>
              <div>
                <div style={{ fontSize: '18px' }} className={streak >= 2 ? 'streak-fire' : ''}>
                  {getStreakEmoji()}
                </div>
                <div style={{ fontSize: '14px', color: isDarkMode ? '#fff' : '#000' }}>{streak}</div>
                <div style={{ fontSize: '9px', color: isDarkMode ? '#666' : '#999' }}>სტრიქი</div>
              </div>
              <div>
                <div style={{ fontSize: '18px' }}>🏆</div>
                <div style={{ fontSize: '14px', color: isDarkMode ? '#fff' : '#000' }}>{bestStreak}</div>
                <div style={{ fontSize: '9px', color: isDarkMode ? '#666' : '#999' }}>საუკეთესო</div>
              </div>
            </div>

            {/* Question */}
            <p style={{ 
              color: isDarkMode ? '#aaa' : '#666',
              fontSize: 'clamp(13px, 1.5vw, 15px)',
              marginBottom: '10px'
            }}>
              {mode === 'key-to-name' 
                ? 'რას ნიშნავს ეს მალსახმობი?' 
                : 'რომელია ამ მალსახმობის კლავიშები?'}
            </p>
            
            {/* Key Display */}
            <div style={{
              fontSize: 'clamp(42px, 6vw, 56px)',
              padding: 'clamp(20px, 3vw, 30px)',
              background: isDarkMode ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.02)',
              borderRadius: '16px',
              margin: '20px 0',
              color: isDarkMode ? '#4ade80' : '#1a1a2e',
              fontFamily: 'monospace',
              border: isDarkMode ? '1px solid rgba(74,222,128,0.1)' : '1px solid rgba(0,0,0,0.05)',
              className: 'key-display'
            }}>
              {mode === 'key-to-name' 
                ? `${shuffledShortcuts[current]?.icon} ${shuffledShortcuts[current]?.key}`
                : `${shuffledShortcuts[current]?.icon} ${shuffledShortcuts[current]?.name}`}
            </div>

            {/* Category Badge */}
            <div style={{
              fontSize: '11px',
              color: isDarkMode ? '#666' : '#999',
              marginBottom: '20px',
              background: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
              padding: '4px 12px',
              borderRadius: '20px',
              display: 'inline-block'
            }}>
              {shuffledShortcuts[current]?.category || 'General'}
            </div>

            {/* Options */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {options.map((value) => {
                const correct = shuffledShortcuts[current];
                const isCorrect = mode === 'key-to-name' 
                  ? value === correct.name 
                  : value === correct.key;
                const isSelected = selected === value;
                let buttonStyle = {
                  padding: 'clamp(14px, 2vw, 18px)',
                  borderRadius: '12px',
                  border: '2px solid',
                  background: isDarkMode ? 'rgba(26,26,46,0.8)' : 'rgba(255,255,255,0.8)',
                  color: isDarkMode ? '#fff' : '#000',
                  cursor: answered ? 'default' : 'pointer',
                  fontSize: 'clamp(15px, 1.5vw, 17px)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  fontWeight: isSelected ? 'bold' : 'normal'
                };

                if (isSelected) {
                  if (isCorrect) {
                    buttonStyle.borderColor = '#4ade80';
                    buttonStyle.background = 'linear-gradient(135deg, #4ade80, #22d3ee)';
                    buttonStyle.color = '#000';
                    buttonStyle.boxShadow = '0 4px 20px rgba(74,222,128,0.3)';
                  } else {
                    buttonStyle.borderColor = '#f87171';
                    buttonStyle.background = 'linear-gradient(135deg, #f87171, #ef4444)';
                    buttonStyle.color = '#fff';
                    buttonStyle.boxShadow = '0 4px 20px rgba(248,113,113,0.3)';
                  }
                } else if (answered && isCorrect) {
                  buttonStyle.borderColor = '#4ade80';
                  buttonStyle.background = 'rgba(74,222,128,0.1)';
                  buttonStyle.boxShadow = '0 0 30px rgba(74,222,128,0.1)';
                }

                return (
                  <button
                    key={value}
                    onClick={() => handleAnswer(value)}
                    style={buttonStyle}
                    disabled={answered}
                    onMouseEnter={(e) => {
                      if (!answered && !isSelected) {
                        e.target.style.transform = 'scale(1.02)';
                        e.target.style.borderColor = '#667eea';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!answered && !isSelected) {
                        e.target.style.transform = 'scale(1)';
                        e.target.style.borderColor = isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
                      }
                    }}
                  >
                    {value}
                    {isSelected && (
                      <span style={{ marginLeft: '10px' }}>
                        {isCorrect ? ' ✅' : ' ❌'}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </>
        ) : (
          <div>
            <div style={{ fontSize: 'clamp(60px, 10vw, 80px)', margin: '20px 0' }}>
              {score >= shortcuts.length * 0.8 ? '🎉' : 
               score >= shortcuts.length * 0.5 ? '😊' : '⌨️'}
            </div>
            <h2 style={{ color: isDarkMode ? '#fff' : '#000' }}>🏁 დასრულდა!</h2>
            <p style={{ 
              fontSize: 'clamp(28px, 5vw, 40px)', 
              color: isDarkMode ? '#fff' : '#000', 
              fontWeight: 'bold' 
            }}>
              {score}/{shortcuts.length}
            </p>
            <p style={{ 
              color: isDarkMode ? '#aaa' : '#666',
              fontSize: 'clamp(14px, 2vw, 18px)'
            }}>
              📊 სიზუსტე: {Math.round((score / shortcuts.length) * 100)}%
            </p>
            <p style={{ 
              color: isDarkMode ? '#aaa' : '#666',
              fontSize: 'clamp(12px, 1.5vw, 14px)'
            }}>
              ⏱️ დრო: {formatTime(time)}
            </p>
            <div style={{
              margin: '15px 0',
              padding: '12px',
              background: isDarkMode ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.03)',
              borderRadius: '10px'
            }}>
              <span style={{ color: '#FFD93D' }}>🏆</span>
              <span style={{ color: isDarkMode ? '#fff' : '#000', marginLeft: '8px' }}>
                საუკეთესო: {bestScore}
              </span>
              {bestStreak > 0 && (
                <span style={{ marginLeft: '15px', color: '#4ade80' }}>
                  🔥 სტრიქი: {bestStreak}
                </span>
              )}
            </div>
            <button onClick={startGame} style={{
              padding: 'clamp(14px, 2vw, 18px) clamp(40px, 6vw, 60px)',
              background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
              color: '#000',
              border: 'none',
              borderRadius: '12px',
              fontSize: 'clamp(16px, 2vw, 18px)',
              cursor: 'pointer',
              marginTop: '20px',
              transition: 'all 0.3s',
              fontWeight: 'bold',
              boxShadow: '0 4px 20px rgba(168,237,234,0.3)'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px) scale(1.02)';
              e.target.style.boxShadow = '0 8px 30px rgba(168,237,234,0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0) scale(1)';
              e.target.style.boxShadow = '0 4px 20px rgba(168,237,234,0.3)';
            }}>
              🔄 თავიდან
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default KeyboardShortcuts;